//* 绘图库
import Konva from "konva";
//* 多语言
import type { Composer } from "vue-i18n";
//* 组件库
import { utility, type UiTooltipFunction } from "various-ui";
//* 组件属性
import type { ModuleTranscript, ModuleFormData } from "../../../src/types";

export interface CanvasRadio extends CanvasData {
    omit?: boolean;
    width: number;
    radio: number;
    offset: number;
}

export interface CanvasData {
    size: number;
    type: "exon" | "intron";
    range: { end: number; start: number };
    codons?: { range: { end: number; start: number }; size: number };
    cds?: {
        size: number;
        range: { end: number; start: number };
        ranges: { end: number; size: number; start: number }[];
    };
}

export type CanvasView = {
    range: { end: number; start: number };
    codons?: { range: { end: number; start: number }; size: number };
    ref?: string;
    alt?: string;
    cds?: {
        size: number;
        range: { end: number; start: number };
        ranges: { end: number; size: number; start: number }[];
    };
};

export class CanvasDispose {
    // * 实例化数据
    stage?: Konva.Stage;
    radios: CanvasRadio[] = [];
    origin: ModuleFormData;
    i18n: Composer;
    data: CanvasData[] = [];
    size: number = 0;
    //* 实例化函数
    constructor(data: ModuleFormData, option: { i18n: Composer; transcript: ModuleTranscript }) {
        //* 数据初始化
        this.i18n = option.i18n;
        this.origin = data;
        console.log("原始数据：", option.transcript);
        this.dispose_areas(option.transcript);
        console.log("区域数据：", this.data);
        this.dispose_ratio();
        console.log("绘图比例：", this.radios);
    }

    //* 静态化数据
    static tips: { data: Ref<CanvasView | undefined>; view?: InstanceType<typeof UiTooltipFunction> } = { data: ref<CanvasRadio>() };

    //* 私有化数组
    private nodes: { container?: Konva.Group; slider?: Konva.Group } = {};

    //* 绘图区域计算
    private dispose_areas(response: ModuleTranscript) {
        //* 临时数据
        const config = { cds: 0, site: 0, codon: 0, codon_diff: 0 };

        //* 遍历生成绘图用的转录本数据
        response.exons.forEach((exon) => {
            //* 数据初始化
            const end = exon[1];
            const start = exon[0];
            const result: CanvasData = { range: { start, end }, size: end - start + 1, type: "exon" };
            //* 检测前端是否存在Intron
            if (config.site + 1 < start) {
                //* 数据更新
                this.data.push({ range: { start: config.site + 1, end: start - 1 }, size: start - config.site - 1, type: "intron" });
                config.site = start - 1;
            }

            //* 获取exon与cds间的重叠区域
            const areas: { start: number; end: number }[] = [];
            response.cds.forEach((c) => {
                //* 检查两个区间是否有重叠
                const range = { end: Math.min(end, c[1]), start: Math.max(start, c[0]) };
                if (range.end >= range.start) {
                    areas.push(range);
                }
            });

            //* 获取exon的cds范围和codon范围
            if (areas.length) {
                //* 获取cds范围
                const start = config.cds + 1;
                const ranges: { end: number; size: number; start: number }[] = [];
                const resource = { site: config.cds, size: 0 };
                areas.forEach((area) => {
                    //* 数据初始化
                    const size = area.end - area.start + 1;
                    //* 数据更新
                    ranges.push(Object.assign(area, { size }));
                    resource.size += size;
                });

                //* cds数据更新
                result.cds = {
                    size: resource.size,
                    range: { start: start, end: start + resource.size - 1 },
                    ranges: ranges,
                };

                //* codon数据更新
                const number = Math.floor((resource.size + config.codon_diff) / 3);
                result.codons = {
                    size: number,
                    range: { start: config.codon + 1, end: config.codon + number },
                };

                //* 配置修改
                config.cds = result.cds.range.end;
                config.codon = result.codons.range.end;
                config.codon_diff = (resource.size + config.codon_diff) % 3;
            }

            //* 数据添加
            this.data.push(result);
            config.site = result.range.end;
        });

        //* 末端特殊处理
        if (config.site < this.origin.sequence.length) {
            this.data.push({
                range: { start: config.site + 1, end: this.origin.sequence.length },
                size: this.origin.sequence.length - config.site,
                type: "intron",
            });
        }
    }

    //* 绘图比例计算
    private dispose_ratio() {
        //* 数据初始化
        this.size = 0;
        this.radios = [];
        //* 临时数据
        const config = { offset: 0, size: 0 };
        //* 遍历预处理绘图数据
        this.data.forEach((value) => {
            switch (value.type) {
                case "exon": {
                    config.size = value.size;
                    if (config.size < 40) config.size = 40;
                    else if (config.size > 360) {
                        config.size = 360;
                    }

                    //* 数据更新
                    this.size += config.size;
                    this.radios.push(Object.assign(value, { width: config.size, radio: config.size / value.size, offset: config.offset }));
                    config.offset += config.size;
                    break;
                }

                case "intron": {
                    if (value.size <= 50) {
                        //* <=50bp固定25px
                        this.size += 25;
                        this.radios.push(Object.assign(value, { width: 25, radio: 25 / value.size, offset: config.offset }));
                        config.offset += 25;
                        break;
                    } else if (value.size <= 100) {
                        //* <=50bp 2:1比例绘制
                        config.size = value.size * 0.5;
                        this.size += config.size;
                        this.radios.push(Object.assign(value, { width: config.size, radio: 0.5, offset: config.offset }));
                        config.offset += config.size;
                        break;
                    } else {
                        //* 左端横线固定25px, 2:1比例绘制
                        this.radios.push({
                            type: "intron",
                            size: 50,
                            width: 25,
                            radio: 0.5,
                            range: { end: value.range.start + 49, start: value.range.start },
                            offset: config.offset,
                        });

                        //* 数据更新
                        this.size += 25;
                        config.offset += 25;

                        //* 中间省略部分按比例绘制
                        config.size = value.size - 100;
                        this.radios.push({
                            type: "intron",
                            omit: true,
                            size: config.size,
                            width: 30,
                            radio: 30 / config.size,
                            range: { end: value.range.end - 50, start: value.range.start + 50 },
                            offset: config.offset,
                        });

                        //* 数据更新
                        this.size += 30;
                        config.offset += 30;

                        //* 右端横线固定25px, 2:1比例绘制
                        this.radios.push({
                            type: "intron",
                            size: 50,
                            width: 25,
                            radio: 0.5,
                            range: { end: value.range.end, start: value.range.end - 49 },
                            offset: config.offset,
                        });

                        //* 数据更新
                        this.size += 25;
                        config.offset += 25;
                        break;
                    }
                }
            }
        });
    }

    //* 绘图坐标转换
    private dispose_site(site: number) {
        const data = this.radios.find((v) => v.range.start <= site && v.range.end >= site);
        if (!data) return;
        else {
            return Object.assign(utility.cloneDeep(data), { site: data.offset + (site - data.range.start) * data.radio });
        }
    }

    //* 获取预测位点文案
    private dispose_context() {
        const ref = this.origin.ref.length > 6 ? `${this.origin.ref.slice(0, 3)}···${this.origin.ref.slice(-3)}` : this.origin.ref;
        const alt = this.origin.alt.length > 6 ? `${this.origin.alt.slice(0, 3)}···${this.origin.alt.slice(-3)}` : this.origin.alt;
        if (!alt) return `${this.i18n.t("Mutant")}: ${ref}del${ref.length}bp`;
        else {
            return `${this.i18n.t("Mutant")}: ${ref} > ${alt} (${this.origin.ref.length}bp > ${this.origin.alt.length}bp)`;
        }
    }

    //* 获取预测位点范围内的Exon区域
    private dispose_site_area() {
        const result: { end: number; start: number; radio: number }[] = [];
        if (!this.origin.data.range) return result;
        else {
            //* 临时数据
            const config = { end: this.origin.data.range.end, start: this.origin.data.range.start };
            this.radios.forEach((v) => {
                if (v.type == "intron") return;
                else {
                    const end = Math.min(config.end, v.range.end);
                    const start = Math.max(config.start, v.range.start);
                    if (end >= start) {
                        result.push({ radio: v.radio, start, end });
                    }
                }
            });
        }

        return result;
    }

    //* 销毁
    destroy() {
        this.stage?.destroy();
    }

    //* 渲染
    render(node: HTMLDivElement, tips?: InstanceType<typeof UiTooltipFunction>) {
        //* 数据初始化
        CanvasDispose.tips.view = tips;
        //* 舞台初始化
        this.stage = new Konva.Stage({ container: node, width: node.clientWidth, height: 154 });
        //* 场景初始化
        const canvas = new Konva.Layer();

        //* 绘制主体
        canvas.add(this.render_body());
        //* 绘制滚动条
        if (this.size > node.clientWidth) {
            canvas.add(this.render_scroll(node));
        }

        //* 绘制预测位点
        this.render_site(node);

        //* 绘制结束
        this.stage.add(canvas);
    }

    //* 主体骨架渲染
    render_body() {
        //* 绘图容器初始化
        this.nodes.container = new Konva.Group();
        //* 临时数据
        const config = { index: 1 };
        //* 遍历数据绘制
        this.radios.forEach((value) => {
            if (!this.nodes.container) return;
            switch (value.type) {
                case "exon": {
                    return this.nodes.container.add(this.render_exon(value, config.index++));
                }

                case "intron": {
                    if (value.omit) return this.nodes.container.add(this.render_intron_omit(value));
                    else {
                        return this.nodes.container.add(this.render_intron(value));
                    }
                }
            }
        });

        return this.nodes.container;
    }

    //* 绘制外显子
    render_exon(value: CanvasRadio, index: number) {
        //* 绘图容器初始化
        const canvas = new Konva.Group();
        //* 文案初始化
        const context = new Konva.Text({ text: "Exon" + index, fill: "#384053", fontSize: 14, fontFamily: "sans-serif" });
        // * 初始化裁剪容器
        const clip = new Konva.Group({
            x: value.offset,
            y: 52,
            //* 裁剪函数
            clipFunc(ctx) {
                //* 设置圆角矩形的参数
                const x = 0.5; //* 矩形左上角的x坐标
                const y = 0.5; //* 矩形左上角的y坐标
                const width = value.width - 1; //* 矩形的宽度
                const height = 23; //* 矩形的高度
                const radius = 2; //* 圆角的半径

                //* 开始新的路径
                ctx.beginPath();
                //* 绘制矩形的左上角到右上角，并添加圆角
                ctx.moveTo(x + radius, y);
                ctx.lineTo(x + width - radius, y);
                //* 使用arcTo绘制圆角
                ctx.arcTo(x + width, y, x + width, y + radius, radius);
                //* 绘制矩形的右上角到右下角，并添加圆角
                ctx.lineTo(x + width, y + height - radius);
                ctx.arcTo(x + width, y + height, x + width - radius, y + height, radius);
                //* 绘制矩形的右下角到左下角，并添加圆角
                ctx.lineTo(x + radius, y + height);
                ctx.arcTo(x, y + height, x, y + height - radius, radius);
                //* 绘制矩形的左下角到左上角，完成路径
                ctx.lineTo(x, y + radius);
                ctx.arcTo(x, y, x + radius, y, radius);
            },
        });

        //* 绘制Exon区域
        clip.add(new Konva.Rect({ fill: "#fff", width: value.width, height: 24 }));
        value.cds?.ranges.forEach((cds) => {
            clip.add(
                new Konva.Rect({
                    x: (cds.start - value.range.start) * value.radio,
                    fill: "#00C7C8",
                    width: cds.size * value.radio,
                    height: 24,
                })
            );
        });

        //* 绘制裁剪后的Exon
        canvas.add(clip);
        // * 绘制Exon边框
        canvas.add(
            new Konva.Rect({
                x: value.offset + 0.5,
                y: 52.5,
                width: value.width - 1,
                height: 23,
                stroke: "rgba(104, 104, 104, 0.50)",
                strokeWidth: 1,
                cornerRadius: 2,
                strokeScaleEnabled: false,
            })
        );

        //* 绘制底部文本
        if (context.getWidth() + 16 <= value.width) {
            canvas.add(context.x(value.offset).y(76).width(value.width).lineHeight(2.14).align("center"));
        }

        //* 悬浮激活窗口
        canvas.on("mouseleave", () => CanvasDispose.tips.view?.hidden());
        canvas.on("mousemove", (ev: Konva.KonvaEventObject<MouseEvent>) =>
            CanvasDispose.tips.view?.show("start", { pageX: ev.evt.pageX, pageY: ev.evt.pageY })
        );
        canvas.on("mouseenter", (ev: Konva.KonvaEventObject<MouseEvent>) => {
            CanvasDispose.tips.data.value = value;
            CanvasDispose.tips.view?.show("start", { pageX: ev.evt.pageX, pageY: ev.evt.pageY, initialization: true });
        });

        return canvas;
    }

    //* 绘制内含子
    render_intron(value: CanvasRadio) {
        return new Konva.Rect({ x: value.offset, y: 63, width: value.width, height: 2, fill: "#686868" });
    }

    //* 绘制内含子（省略部分）
    render_intron_omit(value: CanvasRadio) {
        //* 绘图容器初始化
        const canvas = new Konva.Group();

        canvas.add(new Konva.Rect({ x: value.offset + 6, y: 63, width: 2, height: 2, fill: "#686868", rotation: 2 }));
        canvas.add(new Konva.Rect({ x: value.offset + 14, y: 63, width: 2, height: 2, fill: "#686868", rotation: 2 }));
        canvas.add(new Konva.Rect({ x: value.offset + 22, y: 63, width: 2, height: 2, fill: "#686868", rotation: 2 }));

        return canvas;
    }

    //* 滚动条渲染
    render_scroll(node: HTMLDivElement) {
        //* 绘图容器初始化
        const canvas = new Konva.Group();
        //* 滑块尺寸
        const size = (node.clientWidth * node.clientWidth) / this.size;
        //* 滑块容器初始化
        this.nodes.slider = new Konva.Group({ x: 0, y: 138 });
        //* 绘制滑块内区域
        this.nodes.slider.add(new Konva.Rect({ fill: "#c7c7c7", width: size, height: 14 }));
        this.nodes.slider.add(new Konva.Rect({ x: 1, y: 1, fill: "#fff", width: size - 2, height: 14 }));

        //* 悬浮鼠标样式修改
        this.nodes.slider.on("mouseenter", () => (document.body.style.cursor = "pointer"));
        this.nodes.slider.on("mouseleave", () => (document.body.style.cursor = "auto"));
        //* 滑块拖动反馈
        this.nodes.slider.on("mousedown", (ev: Konva.KonvaEventObject<MouseEvent>) => {
            //* 临时数据
            const config = { site: ev.evt.clientX, offset: -(this.nodes.slider?.offsetX() || 0), result: 0 };
            //* 鼠标移动反馈事件, 边界检测
            document.onmousemove = (ev) => {
                //* 边界检测
                config.result = config.offset + (ev.clientX - config.site);
                if (config.result <= 0) config.result = 0;
                else if (config.result + size >= node.clientWidth) {
                    config.result = node.clientWidth - size;
                }

                //* 触发移动反馈事件
                this.nodes.slider?.offsetX(-config.result);
                this.nodes.container?.offsetX((config.result * this.size) / node.clientWidth);
            };

            //* 鼠标松开反馈事件
            document.onmouseup = () => {
                //* 销毁事件
                document.onmouseup = null;
                document.onmousemove = null;
                //* 还原鼠标样式
                if (ev.evt.target) {
                    document.body.style.cursor = "auto";
                }
            };
        });

        //* 绘制背景
        canvas.add(new Konva.Rect({ x: 0, y: 138, fill: "#c7c7c7", width: node.clientWidth, height: 16 }));
        //* 绘制滑块
        canvas.add(this.nodes.slider);

        return canvas;
    }

    //* 预测位点渲染
    render_site(node: HTMLDivElement) {
        //* 绘图容器初始化
        const canvas = new Konva.Group();
        if (!this.origin.data.range) return;
        else {
            //* 文案初始化
            const context = new Konva.Text({ text: this.dispose_context(), fill: "#fff", fontSize: 14, fontFamily: "sans-serif" });
            //* 临时数据
            const config = { site: -1, size: context.getTextWidth() + 16, offset: -1 };
            //* 绘制柱子、检测位点是否合法、文案位置的初始化
            if (this.origin.data.range.start == this.origin.data.range.end) {
                const result = this.dispose_site(this.origin.data.range.start);
                if (result) {
                    //* 偏移处理
                    config.site = result.site;
                    config.offset = result.site - (config.size - 1) / 2;
                    //* 绘制柱子
                    canvas.add(new Konva.Rect({ x: result.site, y: 25, fill: "#D10000", width: 1, height: result.type == "intron" ? 38 : 51 }));
                }
            } else {
                const end = this.dispose_site(this.origin.data.range.end);
                const start = this.dispose_site(this.origin.data.range.start);
                if (end && start) {
                    //* 尺寸更新、偏移处理
                    config.size = Math.max(config.size, end.site - start.site + 17);
                    config.site = start.site + (end.site - start.site) / 2;
                    config.offset = start.site - (config.size - (end.site - start.site + 1)) / 2;
                    //* 绘制柱子
                    canvas.add(new Konva.Rect({ x: start.site, y: 25, fill: "#D10000", width: 1, height: start.type == "intron" ? 38 : 51 }));
                    canvas.add(new Konva.Rect({ x: end.site, y: 25, fill: "#D10000", width: 1, height: end.type == "intron" ? 38 : 51 }));
                    //* 绘制重叠范围
                    const overlap = this.dispose_site_area();
                    overlap.forEach((range) => {
                        const end = this.dispose_site(range.end);
                        const start = this.dispose_site(range.start);
                        if (end && start) {
                            canvas.add(
                                new Konva.Rect({
                                    x: start.site + 0.5,
                                    y: 52,
                                    fill: "#ff9c00",
                                    width: (range.end - range.start) * range.radio,
                                    height: 24,
                                })
                            );
                        }
                    });
                }
            }

            //* 边界处理
            if (config.offset < 0) config.offset = 0;
            else if (config.offset + config.size > this.size) {
                config.offset = this.size - config.size;
            }

            //* 绘制文案背景
            canvas.add(new Konva.Rect({ x: config.offset, fill: "#D10000", width: config.size, height: 28, cornerRadius: 2 }));
            canvas.add(context.x(config.offset).width(config.size).lineHeight(2.14).align("center"));
            //* 预测位点超过首屏的一半，需定位调整
            if (config.site > node.clientWidth / 2) {
                const offset = ((config.site - node.clientWidth / 2) * node.clientWidth) / this.size;
                this.nodes.slider?.offsetX(-offset);
                this.nodes.container?.offsetX((offset * this.size) / node.clientWidth);
            }
        }

        //* 悬浮激活窗口
        canvas.on("mouseleave", () => CanvasDispose.tips.view?.hidden());
        canvas.on("mousemove", (ev: Konva.KonvaEventObject<MouseEvent>) =>
            CanvasDispose.tips.view?.show("start", { pageX: ev.evt.pageX, pageY: ev.evt.pageY })
        );
        canvas.on("mouseenter", (ev: Konva.KonvaEventObject<MouseEvent>) => {
            CanvasDispose.tips.view?.show("start", { pageX: ev.evt.pageX, pageY: ev.evt.pageY, initialization: true });
            if (this.origin.data.range) {
                CanvasDispose.tips.data.value = { range: this.origin.data.range, ref: this.origin.ref, alt: this.origin.alt };
            }
        });

        //* 加入场景
        this.nodes.container?.add(canvas);
    }
}
